import {z} from '@hono/zod-openapi';

import {ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS} from '../constants/ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS';
import {ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS} from '../constants/ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS';

const assistantEntityReferenceSchema = z.object({
  id: z.string().min(1).max(ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS),
  // Untrusted user/customer-authored display text: render-only, never model instructions.
  label: z.string().min(1).max(ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS).optional(),
});

/**
 * Records which entities the currently mounted page has loaded, so the assistant can identify the
 * current record and propose routes for related records — "open the photos of this case's vehicle" resolves
 * `/vehicles/:id/photos` from `entities.vehicle`, never from the business-case id in the URL.
 *
 * Every id is keyed by its entity kind (`ASSISTANT_CONTEXT_ENTITY_KINDS`); there is no untyped
 * `id`. Route parameters declare the kind they consume (`DmsRouteCatalogEntry.paramEntities`), so a
 * kind mismatch simply leaves the parameter unfilled and drops the route.
 *
 * LIFECYCLE: a page publishes only entities its loaded data identifies and clears the whole slice
 * when it unmounts or switches record, so a stale id cannot outlive its page.
 *
 * TRUST: ids are untrusted model input like the rest of the snapshot, and narrow-only — an id can only fill
 * a parameter of a route that is already in the server catalog AND in `reachableRouteIds`; DMS
 * route guards re-validate at navigation time. The ids themselves are never shown to the model:
 * prompt-bridge resolves paths server-side and the model only ever selects a semantic `routeId`.
 */
export const assistantEntityContextSchema = z
  .object({
    businessCase: assistantEntityReferenceSchema.optional(),
    customer: assistantEntityReferenceSchema.optional(),
    interest: assistantEntityReferenceSchema.optional(),
    rentalOrder: assistantEntityReferenceSchema.optional(),
    vehicle: assistantEntityReferenceSchema.optional(),
  })
  .strict()
  .openapi('AssistantEntityContext');

export type AssistantEntityContext = z.infer<typeof assistantEntityContextSchema>;
